import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { BathroomConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/bathroom-configuration-data';
import { BathroomConfigurationProductDto } from 'src/app/models/configurators-2d/tunnel/bathroom/bathroom-tunnel-dtos';
import { BathroomService } from 'src/app/services/configurators-2d/applications/bathroom.service';
import { CustomerService } from 'src/app/services/customer.service';
import { PrintService } from 'src/app/services/print.service';
import { isGuidValue } from 'src/app/services/helpers/utils';
import { BathroomCartService } from '../services/bathroom-cart.service';
import { TunnelCartService } from '../../common/purchase-list/services/tunnel-cart.service';

/**
 * Étape « Liste d'achat » du tunnel salle de bain.
 * Assemble la recherche produit et le panneau des produits de la configuration.
 */
@Component({
  selector: 'ng-casto-bathroom-tunnel-products',
  templateUrl: './bathroom-tunnel-products.component.html',
  styleUrls: ['./bathroom-tunnel-products.component.scss'],
  providers: [{ provide: TunnelCartService, useExisting: BathroomCartService }],
})
export class BathroomTunnelProductsComponent extends BaseTunnelObject {
  protected readonly bathroomCartService = inject(BathroomCartService);
  private readonly bathroomService = inject(BathroomService);
  private readonly printService = inject(PrintService);
  private readonly translateService = inject(TranslateService);
  private readonly customerService = inject(CustomerService);
  private readonly route = inject(ActivatedRoute);

  protected printLoading: boolean = false;

  private bathroomConfiguration: BathroomConfigurationData;

  constructor() {
    const tunnelNavigationStore = inject(TunnelNavigationStore);
    const tunnelDataStore = inject(TunnelDataStore);
    super(tunnelNavigationStore, tunnelDataStore);
  }

  protected subscribeToObservable(): void {
    const startIndex = this.route.snapshot.params['startIndex'];
    if (isGuidValue(startIndex)) {
      this.bathroomService.loadBathroomConfigurationFromId(startIndex);
    }

    this.subscriptionList.push(
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.bathroomConfiguration = configurationData.get(CONFIG_CONTEXT.BATHROOM_V2) as BathroomConfigurationData;
        }
      })
    );
  }

  /** Enregistre les quantités modifiées avant de passer à l'étape suivante. */
  protected async beforeNextAction(): Promise<void> {
    if (!this.bathroomConfiguration.isLocked) {
      await this.bathroomService.updateBathroomConfiguration(this.bathroomConfiguration);
    }
  }

  /** Imprime la liste d'achat, construite à partir des produits de la configuration. */
  protected printList(): void {
    this.printLoading = true;

    this.printService.reset();
    this.printService
      .addHeader(this.translateService.instant('popins.products_list.print_title_' + this.customerService.context))
      .then(() => {
        this.addCustomerLines();
        this.addProjectLines();
        this.printService.addDiv(this.buildTotalElement());

        return this.printService.addTable(this.buildProductsTable());
      })
      .then(() => {
        this.addDisclaimerLines();
        this.printService.print();
      })
      .finally(() => {
        this.printLoading = false;
      });
  }

  private addCustomerLines(): void {
    if (!this.customerService.isLogged) {
      return;
    }

    this.printService.addText(
      `${this.translateService.instant('popins.products_list.customer_id')} ${this.customerService.userDatas.id}`,
      'my-3'
    );
    this.printService.addText(
      `${this.translateService.instant('popins.products_list.name')} ${this.customerService.fullName}`,
      'my-3'
    );
  }

  private addProjectLines(): void {
    const projectTitle =
      this.bathroomConfiguration?.projectTitle ??
      this.translateService.instant('popins.price_info.placeholder_project_' + this.customerService.context);

    this.printService.addText(`${this.translateService.instant('popins.products_list.project')} ${projectTitle}`, 'my-3');
    this.printService.addText(`Le ${new Date().toLocaleDateString('fr-FR')}`, 'mt-3 mb-5');
  }

  private addDisclaimerLines(): void {
    const disclaimer: string[] = this.translateService
      .instant('popins.products_list.aside.disclaimer')
      .split('<br/>');

    this.printService.addText('', 'mt-5');
    disclaimer.forEach(line => this.printService.addText(line, 'my-3'));
  }

  /** Construit la ligne de total du document imprimé. */
  private buildTotalElement(): HTMLDivElement {
    const total = this.bathroomCartService.total();
    const totalWithDiscount = this.bathroomCartService.totalWithDiscount();

    const totalDiv = document.createElement('div');
    totalDiv.style.textAlign = 'right';
    totalDiv.style.marginBottom = '20px';
    totalDiv.style.fontSize = '18px';
    totalDiv.style.fontWeight = 'bold';
    totalDiv.innerHTML =
      totalWithDiscount !== total
        ? `Total : <span style="text-decoration: line-through; color: #ff354f; margin-right: 8px;">${total.toFixed(2)} €</span> ${totalWithDiscount.toFixed(2)} €`
        : `Total : ${total.toFixed(2)} €`;

    return totalDiv;
  }

  /** Construit le tableau des produits du document imprimé. */
  private buildProductsTable(): HTMLTableElement {
    const table = document.createElement('table');
    table.className = 'products-table';

    const header = table.createTHead().insertRow();
    ['Référence', 'Nom', 'Prix unitaire', 'Quantité', 'Total'].forEach(label => {
      const cell = document.createElement('th');
      cell.textContent = label;
      header.appendChild(cell);
    });

    const body = table.createTBody();
    this.bathroomCartService.products().forEach(product => this.addProductRow(body, product));

    return table;
  }

  private addProductRow(body: HTMLTableSectionElement, product: BathroomConfigurationProductDto): void {
    const salePrice = product.salePrice ?? 0;
    const row = body.insertRow();

    [
      product.range ? `${product.ean} (${product.range})` : product.ean,
      product.label,
      `${salePrice.toFixed(2)} €`,
      `${product.qty}`,
      `${(salePrice * product.qty).toFixed(2)} €`,
    ].forEach(value => (row.insertCell().textContent = value));
  }
}
